/**
 * Distillery detail page component
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, LoadingSpinner, Modal, ConfirmDialog } from '../components/common';
import { DistilleryForm } from '../components/distilleries';
import { useDistillery, useUpdateDistillery, useDeleteDistillery } from '../hooks/useDistilleries';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatters';
import './DistilleryDetail.css';

/**
 * Detail view for a single distillery
 * @returns {React.ReactElement} Page element
 */
export default function DistilleryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { data: distillery, isLoading, error } = useDistillery(id);
  const updateDistillery = useUpdateDistillery();
  const deleteDistillery = useDeleteDistillery();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleEditSubmit = async (formData) => {
    try {
      await updateDistillery.mutateAsync({ id, updates: formData });
      addToast({ type: 'success', message: 'Distillery updated successfully' });
      setShowEditModal(false);
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to update distillery' });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDistillery.mutateAsync(id);
      addToast({ type: 'success', message: 'Distillery deleted successfully' });
      navigate('/distilleries');
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to delete distillery' });
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error || !distillery) {
    return (
      <div className="distillery-detail__error">
        <h2>Distillery Not Found</h2>
        <p>The distillery you&apos;re looking for doesn&apos;t exist.</p>
        <Button onClick={() => navigate('/distilleries')}>
          Back to Distilleries
        </Button>
      </div>
    );
  }

  return (
    <div className="distillery-detail">
      <div className="distillery-detail__header">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          &larr; Back to Distilleries
        </Button>
        <div className="distillery-detail__actions">
          <Button variant="secondary" onClick={() => setShowEditModal(true)}>
            Edit
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
            Delete
          </Button>
        </div>
      </div>

      <div className="distillery-detail__content">
        <h1 className="distillery-detail__name">{distillery.distillery_name}</h1>
        <span className="distillery-detail__country">{distillery.country}</span>

        <div className="distillery-detail__info">
          {distillery.address && (
            <div className="distillery-detail__field">
              <label>Address</label>
              <p>{distillery.address}</p>
            </div>
          )}

          {distillery.website_url && (
            <div className="distillery-detail__field">
              <label>Website</label>
              <p>
                <a href={distillery.website_url} target="_blank" rel="noopener noreferrer">
                  {distillery.website_url}
                </a>
              </p>
            </div>
          )}

          <div className="distillery-detail__row">
            {distillery.city && (
              <div className="distillery-detail__field">
                <label>City</label>
                <p>{distillery.city}</p>
              </div>
            )}

            {distillery.state_region && (
              <div className="distillery-detail__field">
                <label>State / Region</label>
                <p>{distillery.state_region}</p>
              </div>
            )}

            {distillery.year_founded && (
              <div className="distillery-detail__field">
                <label>Year Founded</label>
                <p>{distillery.year_founded}</p>
              </div>
            )}
          </div>
        </div>

        {distillery.notes && (
          <div className="distillery-detail__field">
            <label>Notes</label>
            <p style={{ whiteSpace: 'pre-wrap' }}>{distillery.notes}</p>
          </div>
        )}

        <div className="distillery-detail__meta">
          <p>Created: {formatDate(distillery.created_at)}</p>
          <p>Updated: {formatDate(distillery.updated_at)}</p>
        </div>
      </div>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Distillery"
        size="lg"
      >
        <DistilleryForm
          distillery={distillery}
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditModal(false)}
          loading={updateDistillery.isPending}
        />
      </Modal>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Distillery"
        message={`Are you sure you want to delete "${distillery.distillery_name}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleteDistillery.isPending}
      />
    </div>
  );
}
